/**
 * Design tokens + accessibility contract data (Task 14.3, Req 21.5, 21.6).
 *
 * This module is the single source of truth for the AVOS palette and the
 * contrast contract it must satisfy. It is deliberately DATA-first: the palette
 * and the required-vs-computed contrast pairs are exported as plain objects/
 * arrays so an automated check (Task 17.1) can import them and assert every
 * pair meets its WCAG minimum WITHOUT re-deriving the palette.
 *
 * BRAND (AVOS Brand Guidelines v1.0 — Midhurst Hospitality). The Midhurst
 * palette is inherited unchanged and extended with product neutrals plus one
 * new hue (Clay). Exact hexes:
 *
 *   Deep Emerald  #0B2A1E  application chrome / dark surfaces (sidebar)
 *   Forest        #14402F  headings, primary actions, baseline
 *   Champagne Gold#C9A96A  THE ARCH — exactly one accent per view
 *   Warm Ivory    #F5F4EF  canvas / light background
 *   Sage          #6FA98A  data & illustration only + "matched/healthy" state
 *   Ink           #101A15  body text on ivory (emerald-cast near-black)
 *   Slate         #5C6B63  secondary text, captions, disabled + "pending"
 *   Linen         #EDEBE3  cards & wells on ivory (the one second surface tone)
 *   Rule          #DCD9CE  hairlines, table borders, dividers
 *   Clay          #A6482F  critical / irreversible states only + "unmatched"
 *
 * THE GOLD RULE (§07): gold is not general chrome. It marks the single highest-
 * priority thing on a view — never links, labels, buttons or body text. In this
 * token set gold is therefore a graphical-accent token, not a text/link token,
 * so it is validated (where asserted) against the non-text 3:1 rule.
 *
 * Contrast requirements encoded here (WCAG 2.1 AA):
 *   - Normal text:            ≥ 4.5:1  (Req 21.5)
 *   - Non-text / component /
 *     graphical objects:      ≥ 3:1    (Req 21.5)
 *
 * Ratios are computed with {@link contrastRatio} (pure WCAG relative-luminance
 * formula) rather than hard-coded, so they can never drift from the palette.
 */
import { contrastRatio, type HexColor } from "./contrast.js";

/** WCAG AA minimum contrast for normal text. */
export const TEXT_CONTRAST_MIN = 4.5 as const;

/** WCAG AA minimum contrast for non-text / component / graphical objects. */
export const NON_TEXT_CONTRAST_MIN = 3 as const;

/**
 * The AVOS color palette (brand guidelines v1.0).
 *
 * The generic token names (surface, text, primary, …) are RETAINED so existing
 * component classes keep working; each is mapped onto its brand hue. The brand
 * hues are ALSO exported under their own names so screens can reach for them
 * explicitly (e.g. the emerald sidebar, the one gold element, the sage/clay
 * status colours). Every foreground/background pairing the UI relies on for
 * TEXT clears WCAG AA (see {@link CONTRAST_PAIRS}).
 */
export const COLOR_TOKENS = {
  // --- Generic role tokens (SaaS theme v2 — neutral + blue) --------------
  /** Primary page background / canvas — gray-50 (cool neutral). */
  surface: "#F6F7F9",
  /** Secondary subtle surface (wells, header strips) — gray-50. */
  surfaceMuted: "#F9FAFB",
  /** Default body / strong text — gray-900. */
  text: "#101828",
  /** De-emphasized text (captions, secondary) — gray-500. */
  textMuted: "#667085",
  /** Primary action / link colour (AA on white) — indigo primary-700. */
  primary: "#172B8A",
  /** Text/icon rendered on top of a filled primary control — white. */
  primaryText: "#FFFFFF",
  /** Error / danger / critical foreground — error-600. */
  danger: "#B42318",
  /** Component border / hairline / divider — gray-200+ (stronger). */
  border: "#E4E7EC",
  /** Keyboard focus indicator colour — indigo primary-600. */
  focus: "#1D35A9",

  // --- Legacy brand-hue keys, REMAPPED onto the SaaS palette -------------
  // The key NAMES are retained so every existing `--color-*` consumer keeps
  // working; the VALUES now point at the neutral/blue system. (Old meanings in
  // comments are historical.)
  /** Was emerald sidebar chrome → now the white sidebar/topbar surface. */
  deepEmerald: "#FFFFFF",
  /** Content heading / strong title text (near-black). NOT an interaction
   * colour — blue is reserved for links/CTAs/active states via the primary
   * ramp + --color-primary-action. (Historically the green "forest" brand.) */
  forest: "#1D2939",
  /** Was THE gold accent → now the primary brand indigo (primary-600). */
  gold: "#1D35A9",
  /** Was ivory canvas → now the page background (gray-50). */
  ivory: "#F6F7F9",
  /** Was sage → now success-500 (semantic success only). */
  sage: "#12B76A",
  /** Was ink → now gray-900 (strong text). */
  ink: "#101828",
  /** Was slate → now gray-500 (secondary/muted text). */
  slate: "#667085",
  /** Was linen second surface → now white card surface. */
  linen: "#FFFFFF",
  /** Was rule hairline → now gray-200 border. */
  rule: "#EAECF0",
  /** Was clay critical → now error-600. */
  clay: "#B42318",
  /** Muted label on the (now white) sidebar — gray-500. */
  onEmeraldMuted: "#667085",
  /** Primary text on the (now white) sidebar — gray-700. */
  onEmerald: "#344054",
} as const satisfies Record<string, HexColor>;

/** Token name keys for {@link COLOR_TOKENS}. */
export type ColorTokenName = keyof typeof COLOR_TOKENS;

/** How a color pair is used, selecting which WCAG minimum applies. */
export type ContrastKind = "text" | "non-text";

/** A single documented foreground/background contrast contract. */
export interface ContrastPair {
  /** Human-readable description of where the pair is used. */
  readonly usage: string;
  /** Foreground token. */
  readonly fg: ColorTokenName;
  /** Background token. */
  readonly bg: ColorTokenName;
  /** Whether the text (4.5:1) or non-text (3:1) minimum applies. */
  readonly kind: ContrastKind;
}

/**
 * The exhaustive set of color pairings the shell relies on for TEXT and
 * MEANINGFUL graphical objects, each tagged with the WCAG rule that governs it.
 * Task 17.1 iterates this list and asserts `contrastRatio(fg, bg) >=
 * minimumFor(kind)`.
 *
 * Note on what is (deliberately) NOT listed: per the brand guidelines, gold and
 * sage are "data and illustration only" and gold marks "one element per view" —
 * they are never body text or links, so they are validated as graphical accents
 * only (gold-on-emerald below), never as text. The `Rule #DCD9CE` hairline is a
 * decorative divider (WCAG exempts purely decorative boundaries), so it is not
 * asserted as a meaningful non-text edge. Every status colour is ALWAYS paired
 * with a text label in the UI, so colour is never the sole carrier of meaning.
 */
export const CONTRAST_PAIRS: readonly ContrastPair[] = [
  { usage: "body text (ink) on canvas (ivory)", fg: "text", bg: "surface", kind: "text" },
  { usage: "body text (ink) on card/well (linen)", fg: "text", bg: "surfaceMuted", kind: "text" },
  { usage: "muted/secondary text (slate) on canvas", fg: "textMuted", bg: "surface", kind: "text" },
  { usage: "muted/secondary text (slate) on card/well", fg: "textMuted", bg: "surfaceMuted", kind: "text" },
  { usage: "heading/link/primary (forest) on canvas", fg: "primary", bg: "surface", kind: "text" },
  { usage: "heading/link/primary (forest) on card/well", fg: "primary", bg: "surfaceMuted", kind: "text" },
  { usage: "label on primary control (ivory on forest)", fg: "primaryText", bg: "primary", kind: "text" },
  { usage: "critical/error text (clay) on canvas", fg: "danger", bg: "surface", kind: "text" },
  { usage: "critical/error text (clay) on card/well", fg: "danger", bg: "surfaceMuted", kind: "text" },
  { usage: "sidebar nav text (gray-700) on white sidebar surface", fg: "onEmerald", bg: "deepEmerald", kind: "text" },
  { usage: "sidebar muted label (gray-500) on white sidebar surface", fg: "onEmeraldMuted", bg: "deepEmerald", kind: "text" },
  { usage: "primary accent (blue) on white — component/graphical", fg: "gold", bg: "deepEmerald", kind: "non-text" },
] as const;

/** The WCAG minimum ratio for a given pair kind. */
export function minimumFor(kind: ContrastKind): number {
  return kind === "text" ? TEXT_CONTRAST_MIN : NON_TEXT_CONTRAST_MIN;
}

/** A documented pair with its computed ratio and pass flag. */
export interface DocumentedContrast extends ContrastPair {
  /** Foreground hex value. */
  readonly fgHex: HexColor;
  /** Background hex value. */
  readonly bgHex: HexColor;
  /** The WCAG minimum that applies (4.5 or 3). */
  readonly minimum: number;
  /** The computed WCAG contrast ratio. */
  readonly ratio: number;
  /** Whether `ratio >= minimum`. */
  readonly passes: boolean;
}

/**
 * Compute the documented contrast table: every pair in {@link CONTRAST_PAIRS}
 * resolved to hex values with its computed ratio, applicable minimum, and a
 * pass flag. This is the machine-readable artifact Task 17.1 asserts against.
 *
 * Computed at call time (cheap, pure) so the ratios always reflect the current
 * {@link COLOR_TOKENS} values.
 */
export function documentedContrasts(): readonly DocumentedContrast[] {
  return CONTRAST_PAIRS.map((pair) => {
    const fgHex = COLOR_TOKENS[pair.fg];
    const bgHex = COLOR_TOKENS[pair.bg];
    const minimum = minimumFor(pair.kind);
    const ratio = contrastRatio(fgHex, bgHex);
    return {
      ...pair,
      fgHex,
      bgHex,
      minimum,
      ratio,
      passes: ratio >= minimum,
    };
  });
}

/**
 * Documented computed contrast ratios (informational; recomputed by
 * {@link documentedContrasts}). Kept in the doc comment so reviewers see the
 * numbers without running code:
 *
 *   ink on ivory (body) ..................... 16.15:1  (≥ 4.5) ✓
 *   ink on linen (body on card) ............. 14.90:1  (≥ 4.5) ✓
 *   slate on ivory (muted) ..................  5.10:1  (≥ 4.5) ✓
 *   slate on linen (muted on card) ..........  4.70:1  (≥ 4.5) ✓
 *   forest on ivory (heading/link) .......... 10.57:1  (≥ 4.5) ✓
 *   forest on linen (heading/link on card) ..  9.75:1  (≥ 4.5) ✓
 *   ivory on forest (button label) .......... 10.57:1  (≥ 4.5) ✓
 *   clay on ivory (critical text) ...........  5.31:1  (≥ 4.5) ✓
 *   clay on linen (critical on card) ........  4.90:1  (≥ 4.5) ✓
 *   ivory on emerald (sidebar text) ......... 13.98:1  (≥ 4.5) ✓
 *   sage-tint on emerald (sidebar label) ....  6.93:1  (≥ 4.5) ✓
 *   gold on emerald (the one accent) ........  6.87:1  (≥ 3)   ✓
 */
export const DOCUMENTED_CONTRAST_RATIOS = "see documentedContrasts()" as const;
